import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Login() {
  const { login, signup } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);

  const handleGoogle = () => {
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submitLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Welcome back');
      navigate(sessionStorage.getItem('post_login_redirect') || '/');
      sessionStorage.removeItem('post_login_redirect');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Sign-in failed');
    } finally { setLoading(false); }
  };

  const submitSignup = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await signup(email, password, name);
      toast.success('Account created');
      navigate(sessionStorage.getItem('post_login_redirect') || '/');
      sessionStorage.removeItem('post_login_redirect');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Sign-up failed');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-[85vh] grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img src="https://images.unsplash.com/photo-1773227054058-afd18b554e8f?crop=entropy&cs=srgb&fm=jpg&q=85" alt="Supportive community" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-primary/70" />
        <div className="relative z-10 p-12 h-full flex flex-col justify-end text-primary-foreground">
          <div className="text-sm opacity-80 mb-2">AccessBenefits Ontario</div>
          <h2 className="text-4xl font-bold leading-tight" style={{fontFamily: 'Lexend'}}>One profile.<br />Every benefit you may qualify for.</h2>
          <p className="mt-4 opacity-90 max-w-md">Your data stays yours. We never ask for CRA, Service Canada, or banking passwords.</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground" data-testid="back-home-link">← Back to home</Link>
          <h1 className="text-3xl font-bold mt-4" style={{fontFamily: 'Lexend'}}>Sign in to continue</h1>
          <p className="text-muted-foreground mt-2">Save your profile, track applications, and pick up where you left off.</p>

          <Button onClick={handleGoogle} data-testid="google-signin-btn" className="mt-6 w-full h-12 bg-card border border-border text-foreground hover:bg-secondary" variant="outline">
            <svg className="w-5 h-5 mr-3" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
            Continue with Google
          </Button>

          <div className="my-6 flex items-center gap-3 text-xs text-muted-foreground">
            <div className="h-px flex-1 bg-border" /> or use email <div className="h-px flex-1 bg-border" />
          </div>

          <Tabs defaultValue="signin">
            <TabsList className="grid grid-cols-2 mb-4">
              <TabsTrigger value="signin" data-testid="tab-signin">Sign in</TabsTrigger>
              <TabsTrigger value="signup" data-testid="tab-signup">Create account</TabsTrigger>
            </TabsList>
            <TabsContent value="signin">
              <form onSubmit={submitLogin} className="space-y-4">
                <div>
                  <Label htmlFor="e">Email</Label>
                  <Input id="e" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" className="h-11 mt-1.5" />
                </div>
                <div>
                  <Label htmlFor="p">Password</Label>
                  <Input id="p" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" className="h-11 mt-1.5" />
                </div>
                <Button type="submit" disabled={loading} data-testid="login-submit" className="w-full h-11 bg-primary hover:bg-primary/90 text-primary-foreground">{loading ? 'Signing in…' : 'Sign in'}</Button>
              </form>
            </TabsContent>
            <TabsContent value="signup">
              <form onSubmit={submitSignup} className="space-y-4">
                <div>
                  <Label htmlFor="n">Full name</Label>
                  <Input id="n" required value={name} onChange={(e) => setName(e.target.value)} data-testid="signup-name" className="h-11 mt-1.5" />
                </div>
                <div>
                  <Label htmlFor="e2">Email</Label>
                  <Input id="e2" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="signup-email" className="h-11 mt-1.5" />
                </div>
                <div>
                  <Label htmlFor="p2">Password</Label>
                  <Input id="p2" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="signup-password" className="h-11 mt-1.5" />
                </div>
                <Button type="submit" disabled={loading} data-testid="signup-submit" className="w-full h-11 bg-primary hover:bg-primary/90 text-primary-foreground">{loading ? 'Creating…' : 'Create account'}</Button>
              </form>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
